import { useState } from "react";

import { useToast } from "../../../hooks/useToast";
import { serverErrorMessage } from "../../../utils/HttpError";
import {
  FilterButton,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../shared";
import { ModalBodyText } from "./AdminTranslations.styles";

interface IConfirmSyncAllModalProps {
  keyCount: number;
  environmentName: string;
  isProductionEnvironment: boolean;
  onConfirm: () => Promise<string>;
  onClose: () => void;
}

/**
 * Confirms the bulk "sync everything pending" action (EN-5178). Every key it
 * touches is written to THIS environment's Global DB — in production that is
 * live content, hence the louder warning there.
 */
export function ConfirmSyncAllModal({
  keyCount,
  environmentName,
  isProductionEnvironment,
  onConfirm,
  onClose,
}: IConfirmSyncAllModalProps) {
  const { showToast } = useToast();
  const [isSyncing, setIsSyncing] = useState(false);

  const handleConfirm = async () => {
    setIsSyncing(true);
    try {
      const summary = await onConfirm();
      showToast(`Sync finished: ${summary}.`, "success");
      onClose();
    } catch (error) {
      showToast(
        serverErrorMessage(error) ?? "The sync failed. Nothing was changed.",
        "warning"
      );
      setIsSyncing(false);
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Sync all pending translations?</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Pulls <strong>{keyCount}</strong> key{keyCount === 1 ? "" : "s"} with
          missing or different translations from the master store into the{" "}
          <strong>{environmentName}</strong> environment.
        </ModalBodyText>
        {isProductionEnvironment && (
          <WarningNotice>
            This is PRODUCTION — the values go live for patients and clinicians
            as soon as they land.
          </WarningNotice>
        )}
        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSyncing}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isSyncing}
            onClick={handleConfirm}
          >
            {isSyncing ? "Syncing..." : "Sync all"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
